/** Feature API or state module: availableRolesSlice.ts. */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Role {
  id: number;
  name: string;
}

export const APPLICATION_ROLES = {
  ['Super Admin']: { id: 1, name: 'Super Admin' },
  ['Master']: { id: 2, name: 'Master' },
  ['User']: { id: 3, name: 'User' },
} as const satisfies Record<string, Role>;

export type ApplicationRole = keyof typeof APPLICATION_ROLES;

export const normalizeRoleName = (roleName: string): ApplicationRole | null => {
  const normalized = roleName
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

  if (normalized === 'SUPER_ADMIN') return 'Super Admin';
  if (normalized === 'MASTER') return 'Master';
  if (normalized === 'USER') return 'User';

  return null;
};

export const getAvailableRoles = (roleName: string): Role[] => {
  const role = normalizeRoleName(roleName);
  if (role === 'Super Admin') return [APPLICATION_ROLES.Master, APPLICATION_ROLES.User];
  if (role === 'Master') return [APPLICATION_ROLES.User];

  return [];
};

export const getApplicationRoleById = (roleId: number): ApplicationRole | null => {
  const role = Object.entries(APPLICATION_ROLES).find(([, value]) => value.id === roleId);
  return (role?.[0] as ApplicationRole | undefined) ?? null;
};

interface RoleState {
  availableRoles: Role[];
}

const initialState: RoleState = {
  availableRoles: [],
};

const roleSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setAvailableRoles(state, action: PayloadAction<Role[]>) {
      state.availableRoles = action.payload;
    },
    clearAvailableRoles(state) {
      state.availableRoles = [];
    },
  },
});

export const { setAvailableRoles, clearAvailableRoles } = roleSlice.actions;
export const roleReducer = roleSlice.reducer;
